export function formatCurrency(amount: number | string | undefined, currency: string | null | undefined) {
  const num = typeof amount === "string" ? parseFloat(amount) : (amount ?? 0);
  // No symbol rather than a default "£": card spend is routinely foreign, and a wrong symbol states something false.
  if (!currency) {
    return num.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  const symbol = currency === "GBP" ? "£" : currency === "USD" ? "$" : currency === "EUR" ? "€" : currency;
  // Sign before the symbol: -£1.99, not £-1.99.
  const sign = num < 0 ? "-" : "";
  return `${sign}${symbol}${Math.abs(num).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

type UnitOfMeasure = "Gram" | "Kilogram" | "Millilitre" | "Litre" | null | undefined;

/** Short symbol for the structured weight/volume unit we parse for catch-weight matching. */
export function unitOfMeasureAbbr(uom: UnitOfMeasure): string | null {
  switch (uom) {
    case "Gram":
      return "g";
    case "Kilogram":
      return "kg";
    case "Millilitre":
      return "mL";
    case "Litre":
      return "L";
    default:
      return null;
  }
}

/** Mirror of the backend UnitParser.Parse; keep in sync with Dolfin.Common.UnitParser. */
export function parseUnitOfMeasure(unit: string | null | undefined): UnitOfMeasure {
  if (!unit || !unit.trim()) return null;
  const t = unit.trim().toLowerCase().replace(/[.s ]+$/, "");
  switch (t) {
    case "kg":
    case "kilo":
    case "kilogram":
    case "kilogramme":
      return "Kilogram";
    case "g":
    case "gr":
    case "gram":
    case "gramme":
      return "Gram";
    case "ml":
    case "milliliter":
    case "millilitre":
      return "Millilitre";
    case "l":
    case "ltr":
    case "liter":
    case "litre":
      return "Litre";
    default:
      return null;
  }
}

/** Prefers the structured `unitOfMeasure` ("2 kg"), falling back to the free-text `unit`. */
export function formatQuantity(
  quantity: number | string | null | undefined,
  unit?: string | null,
  unitOfMeasure?: UnitOfMeasure,
): string {
  const qty = typeof quantity === "string" ? parseFloat(quantity) : (quantity ?? 0);
  const qtyText = Number.isNaN(qty) ? String(quantity ?? "") : String(qty);
  const abbr = unitOfMeasureAbbr(unitOfMeasure);
  if (abbr) return `${qtyText} ${abbr}`;
  if (unit) return `${qtyText} ${unit}`;
  return qtyText;
}

// en-GB ICU now writes September as "Sept"; one abbreviation everywhere, as the date pickers (date-fns) show.
const sep = (formatted: string) => formatted.replace(/\bSept\b/, "Sep");

export function formatDate(dateStr: string | undefined | null, month: "short" | "long" = "short") {
  if (!dateStr) return "N/A";
  return sep(
    new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month,
      year: "numeric",
    }),
  );
}

// The API sends calendar days as "2026-10-01" or midnight UTC; through the local clock they slip a day west of UTC.

/** Whole days from today (the viewer's calendar day) to the date's calendar day. Negative when past. */
export function daysFromToday(dateStr: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!m) return null;
  const day = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((day - today) / 86_400_000);
}

/** A calendar date ("1 Oct 2026") without the viewer's time zone shifting it. Blank for an invalid date. */
export function formatCalendarDate(date: Date | string, month: "short" | "long" = "short") {
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  return sep(d.toLocaleDateString("en-GB", { day: "numeric", month, year: "numeric", timeZone: "UTC" }));
}

export function formatTime(dateStr: string | undefined | null) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export function formatDateTime(dateStr: string | undefined | null, month: "short" | "long" = "short") {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  const date = sep(
    d.toLocaleDateString("en-GB", {
      day: "numeric",
      month,
      year: "numeric",
    }),
  );
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date}, ${time}`;
}

export function formatBytes(bytes: number | string): string {
  const n = typeof bytes === "string" ? parseInt(bytes, 10) : bytes;
  if (!Number.isFinite(n) || n <= 0) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
